import type { DeviceContext } from "../lib/device";
import { locationChip, type LocationReadiness } from "../capture/use-location-status";

// Field Status presentation model — the support-safe answer to "why is NFC/QR/location/offline
// not behaving on this phone?". Pure: facts in, labeled rows out, so every platform permutation
// is unit-testable without a browser. Honesty rules carried from the capture doctrine:
//   - capability gaps are field realities, not failures → muted tone, never error styling;
//   - never imply an unsupported browser feature might work (iOS Web NFC, insecure contexts);
//   - values are labels/counts only — no identities, tokens, keys, or coordinates.

export interface FieldStatusFacts {
  appVersion: string;
  /** Host only (e.g. "secops-guard.pages.dev") — never path, query, or credentials. */
  originHost: string;
  isSecureContext: boolean;
  isIosWebKit: boolean;
  isOnline: boolean;
  device: DeviceContext;
  location: { readiness: LocationReadiness; accuracyMeters: number | null };
  /** Captures persisted on this phone still waiting to sync (queued + interrupted). */
  pendingQueueCount: number;
  rejectedQueueCount: number;
  checkpointCount: number;
  hasRouteAssignment: boolean;
  /** Resolved tenant/user method gates — policy wins over hardware in the method rows. */
  captureFlags: { qrCapture: boolean; nfcCapture: boolean; manualCapture: boolean };
  /** Timeclock self-service flag (web_timeclock) — dark by default. */
  timeclockEnabled: boolean;
}

export interface FieldStatusRow {
  key: string;
  label: string;
  value: string;
  tone: "ok" | "info" | "muted";
  detail?: string;
  /** True when the duty screen offers a fix the guard can tap (e.g. the location prompt). */
  actionable?: boolean;
}

export interface FieldStatusSection {
  title: string;
  rows: FieldStatusRow[];
}

export function fieldStatusSections(facts: FieldStatusFacts): FieldStatusSection[] {
  return [
    {
      title: "App",
      rows: [versionRow(facts), connectionRow(facts), displayModeRow(facts), networkRow(facts), notificationsRow()],
    },
    {
      title: "Capture methods",
      rows: [cameraRow(facts), nfcRow(facts), manualRow(facts), timeclockRow(facts), locationRow(facts)],
    },
    { title: "Sync", rows: [queueRow(facts)] },
  ];
}

function versionRow(facts: FieldStatusFacts): FieldStatusRow {
  return {
    key: "version",
    label: "App version",
    value: `v${facts.appVersion}`,
    tone: "ok",
    detail: "Share this line when reporting an issue.",
  };
}

function connectionRow(facts: FieldStatusFacts): FieldStatusRow {
  if (facts.isSecureContext) {
    return {
      key: "connection",
      label: "Connection",
      value: "Secure (HTTPS)",
      tone: "ok",
      detail: facts.originHost === "" ? "Encrypted connection." : `Encrypted connection to ${facts.originHost}.`,
    };
  }
  return {
    key: "connection",
    label: "Connection",
    value: "Not secure",
    tone: "muted",
    detail: "Camera, NFC, and location are blocked by the browser on insecure pages — open the official HTTPS address.",
  };
}

function displayModeRow(facts: FieldStatusFacts): FieldStatusRow {
  if (facts.device.isStandalonePwa) {
    return {
      key: "display-mode",
      label: "Display mode",
      value: "Installed app",
      tone: "ok",
      detail: "Running standalone from the Home Screen.",
    };
  }
  return {
    key: "display-mode",
    label: "Display mode",
    value: "Browser tab",
    tone: "info",
    detail: facts.isIosWebKit
      ? "Install it: open in Safari, then Share → Add to Home Screen."
      : "Install it from the browser menu for a full-screen app.",
  };
}

function networkRow(facts: FieldStatusFacts): FieldStatusRow {
  if (facts.isOnline) {
    return { key: "network", label: "Network", value: "Online", tone: "ok", detail: "Captures sync immediately." };
  }
  // Offline is a supported duty state, not an error — the queue carries the shift.
  return {
    key: "network",
    label: "Network",
    value: "Offline",
    tone: "muted",
    detail: "Captures keep working and queue on this phone until the connection returns.",
  };
}

/** Honest until the Web Push client wiring exists: notifications are NOT yet a web feature,
 *  whatever the browser permission says. The row flips to capability reporting when that lands. */
function notificationsRow(): FieldStatusRow {
  return {
    key: "notifications",
    label: "Notifications",
    value: "Not yet available on web",
    tone: "muted",
    detail: "Alerts reach the native app and the supervisor console today; browser notifications for Guard Web are planned.",
  };
}

// Policy-off beats hardware state in every method row: "your account turns this off" is the
// actionable truth, and claiming hardware availability for a flagged-off method would be a lie.
const POLICY_OFF_VALUE = "Disabled by account policy";

function cameraRow(facts: FieldStatusFacts): FieldStatusRow {
  if (!facts.captureFlags.qrCapture) {
    return {
      key: "camera-qr",
      label: "QR camera",
      value: POLICY_OFF_VALUE,
      tone: "muted",
      detail: "Your account turns QR capture off on web — ask your supervisor if that seems wrong.",
    };
  }
  if (facts.device.hasCamera) {
    return {
      key: "camera-qr",
      label: "QR camera",
      value: "Available",
      tone: "ok",
      detail: "Point at a checkpoint QR label to capture.",
    };
  }
  return {
    key: "camera-qr",
    label: "QR camera",
    value: "Not available",
    tone: "muted",
    detail: "This browser exposes no camera — use manual check-in.",
  };
}

function nfcRow(facts: FieldStatusFacts): FieldStatusRow {
  if (!facts.captureFlags.nfcCapture) {
    return {
      key: "nfc-tap",
      label: "NFC tap",
      value: POLICY_OFF_VALUE,
      tone: "muted",
      detail: "Your account turns NFC off on web — ask your supervisor if that seems wrong.",
    };
  }
  if (facts.device.hasWebNfc) {
    return {
      key: "nfc-tap",
      label: "NFC tap",
      value: "Available",
      tone: "ok",
      detail: "Hold a checkpoint tag to the back of the phone.",
    };
  }
  if (!facts.isSecureContext) {
    return {
      key: "nfc-tap",
      label: "NFC tap",
      value: "Blocked (insecure page)",
      tone: "muted",
      detail: "Web NFC needs an HTTPS connection.",
    };
  }
  if (facts.isIosWebKit) {
    return {
      key: "nfc-tap",
      label: "NFC tap",
      value: "Not available on iPhone or iPad",
      tone: "muted",
      detail: "No iOS browser can read NFC tags — QR and manual check-in cover this device.",
    };
  }
  return {
    key: "nfc-tap",
    label: "NFC tap",
    value: "Not available in this browser",
    tone: "muted",
    detail: "Android Chrome supports tap to scan; QR and manual check-in work here.",
  };
}

function manualRow(facts: FieldStatusFacts): FieldStatusRow {
  if (!facts.captureFlags.manualCapture) {
    return {
      key: "manual",
      label: "Manual check-in",
      value: POLICY_OFF_VALUE,
      tone: "muted",
      detail: "Your account turns manual check-in off on web — ask your supervisor if that seems wrong.",
    };
  }
  if (facts.checkpointCount > 0) {
    const plural = facts.checkpointCount === 1 ? "checkpoint" : "checkpoints";
    return {
      key: "manual",
      label: "Manual check-in",
      value: `${facts.checkpointCount} ${plural} ready`,
      tone: "ok",
      detail: "Submit from the checkpoint list when scanning isn't possible.",
    };
  }
  if (facts.hasRouteAssignment) {
    return {
      key: "manual",
      label: "Manual check-in",
      value: "Route loaded — no checkpoints yet",
      tone: "muted",
      detail: "Ask your supervisor to add checkpoints to this route.",
    };
  }
  return {
    key: "manual",
    label: "Manual check-in",
    value: "No route assigned",
    tone: "muted",
    detail: "Ask your supervisor for a route assignment.",
  };
}

function timeclockRow(facts: FieldStatusFacts): FieldStatusRow {
  if (!facts.timeclockEnabled) {
    return {
      key: "timeclock",
      label: "Timeclock",
      value: POLICY_OFF_VALUE,
      tone: "muted",
      detail: "Your account turns the web timeclock off — punches happen in the app or not at all.",
    };
  }
  return {
    key: "timeclock",
    label: "Timeclock",
    value: "Available — online only",
    tone: "ok",
    detail: "Clock in/out and breaks submit immediately; web punches are never queued offline.",
  };
}

function locationRow(facts: FieldStatusFacts): FieldStatusRow {
  // Reuse the duty-screen chip semantics so Status and the capture header never disagree.
  const chip = locationChip({
    readiness: facts.location.readiness,
    accuracyMeters: facts.location.accuracyMeters,
    checking: false,
    requestLocation: async () => {},
  });
  const detail =
    facts.location.readiness === "granted"
      ? "Captures attach GPS evidence automatically."
      : facts.location.readiness === "denied"
        ? "The server flags captures without GPS instead of rejecting them."
        : facts.location.readiness === "unavailable"
          ? "This browser exposes no location service."
          : "Grant location before your first capture so it doesn't stall at the checkpoint.";
  return {
    key: "location",
    label: "Location",
    value: chip.label,
    tone: chip.tone,
    detail,
    actionable: chip.actionable,
  };
}

function queueRow(facts: FieldStatusFacts): FieldStatusRow {
  const rejectedNote =
    facts.rejectedQueueCount > 0
      ? ` ${facts.rejectedQueueCount} rejected by the server and kept for supervisor review.`
      : "";
  if (facts.pendingQueueCount > 0) {
    return {
      key: "offline-queue",
      label: "Offline queue",
      value: `${facts.pendingQueueCount} saved on this phone`,
      tone: "info",
      detail: `Sync runs automatically when the phone is back online.${rejectedNote}`,
    };
  }
  if (facts.rejectedQueueCount > 0) {
    return {
      key: "offline-queue",
      label: "Offline queue",
      value: "Nothing waiting",
      tone: "muted",
      detail: rejectedNote.trim(),
    };
  }
  return {
    key: "offline-queue",
    label: "Offline queue",
    value: "Empty — all captures synced",
    tone: "ok",
    detail: "Offline captures are kept on this phone and replayed in order.",
  };
}

/** Window-shaped slice we read — injectable for tests. */
interface WindowFactsLike {
  isSecureContext?: boolean;
  location?: { host?: string };
  navigator?: { onLine?: boolean; userAgent?: string; maxTouchPoints?: number };
}

/** Read the runtime facts only a live window can answer. Fails closed (insecure) when absent. */
export function readRuntimeFacts(win: Window): {
  originHost: string;
  isSecureContext: boolean;
  isIosWebKit: boolean;
  isOnline: boolean;
} {
  const w = win as WindowFactsLike;
  return {
    originHost: w.location?.host ?? "",
    isSecureContext: w.isSecureContext === true,
    isIosWebKit: detectIosWebKit(win),
    // Mirror the offline queue's stance: only an explicit false counts as offline.
    isOnline: w.navigator?.onLine !== false,
  };
}

/** iPhone/iPad detection — iPadOS reports a macOS user agent but keeps its touch points. */
export function detectIosWebKit(win: Window): boolean {
  const nav = (win as WindowFactsLike).navigator;
  const ua = nav?.userAgent ?? "";
  if (/iPad|iPhone|iPod/.test(ua)) {
    return true;
  }
  return /Mac/.test(ua) && (nav?.maxTouchPoints ?? 0) > 1;
}
